import {
	Card,
	CardBody,
	Button,
} from '@windmill/react-ui';
import Select from 'react-select';
import VendorLedgerPipeline from 'components/vendorledger/VendorLedgerPipeline';
import VendorLedgerSummaryTable from 'components/vendorledger/VendorLedgerSummaryTable';
import VendorLedgerPaymentsList from 'components/vendorledger/VendorLedgerPaymentsList';
import DataWrapper from './DataWrapper';
import NotFound from 'components/table/NotFound';
import PageTitle from 'components/Typography/PageTitle';
import { useState, useEffect } from 'react';
import VendorLedgerServices from 'services/VendorLedgerServices';
import { FiRefreshCcw } from 'react-icons/fi';
import sidebar from 'routes/sidebar';
import LabelArea from 'components/form/LabelArea';
import InputSearchDate from 'components/form/InputSearchDate';

const customSelectStyles = {
	control: (base) => ({
		...base,
		border: '1px',
		boxShadow: 'none',
		backgroundColor: 'RRGGBB',
	}),
	option: (provided, state) => ({
		...provided,
		background: state.isSelected
			? '#0e9f6e'
			: state.isFocused
				? 'rgba(0, 0, 0, 0.15)'
				: 'white',
		color: state.isSelected ? 'white' : 'black',
		'&:hover': {
			background: 'rgba(0, 0, 0, 0.15)',
			color: 'black',
		},
	}),
	menu: (provided) => ({
		...provided,
		margin: '10px 0 -10px 0',
	}),
	// Rendered via menuPortalTarget (see below) to escape the parent Card's
	// overflow-hidden, which otherwise clips the dropdown list invisibly.
	menuPortal: (base) => ({
		...base,
		zIndex: 9999,
	}),
	indicatorSeparator: () => ({
		display: 'none',
	}),
};

const VendorLedger = () => {
	const adminInfo = localStorage.getItem('adminInfo')
		? JSON.parse(localStorage.getItem('adminInfo'))
		: null;
	const isAdmin = adminInfo?.id === 1;

	const hideView = isAdmin
		? true
		: adminInfo?.permission?.some(
				(perm) => perm.id_tab === 'ledger' && perm.view === 1
			);

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [status, setStatus] = useState(false);

	const [vendorOptions, setVendorOptions] = useState([]);
	const [selectedVendor, setSelectedVendor] = useState(null);

	const [ledgerData, setLedgerData] = useState(null);

	const [searchstartDate, setSearchStartDate] = useState(null);
	const [searchendDate, setSearchEndDate] = useState(null);

	// Vendor list for the dropdown — same source as the rest of the admin panel's vendor selects.
	useEffect(() => {
		const fetchVendors = async () => {
			try {
				const response = await VendorLedgerServices.getAllVendorName();
				const options = Array.isArray(response)
					? response.map((v) => ({ value: v.id, label: v.company_name }))
					: [];
				setVendorOptions(options);
			} catch (err) {
				console.error('Error fetching vendors:', err);
			}
		};
		fetchVendors();
	}, []);

	const formatDate = (date) => {
		if (!date) return null;
		const d = new Date(date);
		const day = String(d.getDate()).padStart(2, '0');
		const month = String(d.getMonth() + 1).padStart(2, '0');
		const year = d.getFullYear();
		return `${day}-${month}-${year}`;
	};

	// Ledger is only fetched for the vendor actually selected — never a combined/all-vendors view.
	const fetchLedger = async (vendorId) => {
		if (!vendorId) {
			setLedgerData(null);
			return;
		}
		setLoading(true);
		setError(false);
		try {
			const params = {};
			if (searchstartDate && searchendDate) {
				params.from = formatDate(searchstartDate);
				params.to = formatDate(searchendDate);
			}
			const response = await VendorLedgerServices.getVendorLedger(vendorId, params);
			setLedgerData(response?.data || null);
			setStatus(response?.status);
		} catch (err) {
			console.error('Error fetching vendor ledger:', err);
			setError(true);
			setStatus(false);
		} finally {
			setLoading(false);
		}
	};

	const handleVendorChange = (selected) => {
		setSelectedVendor(selected);
		fetchLedger(selected?.value);
	};

	const handleSearch = () => {
		fetchLedger(selectedVendor?.value);
	};

	const handleRefresh = () => {
		setSearchStartDate(null);
		setSearchEndDate(null);
		setSelectedVendor(null);
		setLedgerData(null);
	};

	const entries = ledgerData?.entries || [];

	return (
		<>
			{hideView && (
			<>
			<PageTitle sidebar={sidebar().sidebar} />
			<Card className="min-w-0 shadow-xs overflow-hidden bg-white dark:bg-gray-800">
				<CardBody>
					<div className="flex flex-wrap items-center gap-3">
						<div className="flex-1 min-w-[220px]">
							{/* <LabelArea label="Vendor" /> */}
							<Select
								className="block w-full focus:outline-none dark:text-gray-300 leading-5 rounded-md focus:border-gray-200 border-gray-200 dark:border-gray-600 focus:ring focus:ring-green-300 dark:focus:border-gray-500 dark:focus:ring-gray-300 border h-10 text-sm bg-white-100 dark:bg-white border-transparent focus:bg-white"
								styles={customSelectStyles}
								options={vendorOptions}
								value={selectedVendor}
								onChange={handleVendorChange}
								placeholder="Select Vendor"
								isClearable
								menuPortalTarget={document.body}
								menuPosition="fixed"
							/>
						</div>

						<div className="flex items-center gap-2">
							<LabelArea label="Start Date" />
							<div className="w-50">
								<InputSearchDate
									value={searchstartDate}
									onChange={(event) => setSearchStartDate(event)}
								/>
							</div>
						</div>

						<div className="flex items-center gap-2">
							<LabelArea label="End Date" />
							<div className="w-50">
								<InputSearchDate
									value={searchendDate}
									min={searchstartDate}
									onChange={(event) => setSearchEndDate(event)}
								/>
							</div>
						</div>

						<div>
							<Button
								className="rounded-md h-10 px-8"
								disabled={!selectedVendor}
								onClick={handleSearch}
							>
								Search
							</Button>
						</div>

						<FiRefreshCcw onClick={handleRefresh} cursor="pointer" />
					</div>
				</CardBody>
			</Card>

			{!selectedVendor ? (
				<NotFound title="Select a vendor to view their ledger." />
			) : (
				<>
					{ledgerData && (
						<div className="grid grid-cols-3 gap-4 my-4">
							<Card className="bg-white dark:bg-gray-800">
								<CardBody>
									<p className="text-sm text-gray-500">Total Debit</p>
									<p className="text-lg font-semibold text-red-600">
										₹{ledgerData.total_debit}
									</p>
								</CardBody>
							</Card>
							<Card className="bg-white dark:bg-gray-800">
								<CardBody>
									<p className="text-sm text-gray-500">Total Credit</p>
									<p className="text-lg font-semibold text-green-600">
										₹{ledgerData.total_credit}
									</p>
								</CardBody>
							</Card>
							<Card className="bg-white dark:bg-gray-800">
								<CardBody>
									<p className="text-sm text-gray-500">Closing Balance</p>
									<p className="text-lg font-semibold">
										₹{ledgerData.closing_balance}
									</p>
								</CardBody>
							</Card>
						</div>
					)}

					<DataWrapper loading={loading} error={error} status={status}>
						{entries.length !== 0 ? (
							<>
								<VendorLedgerSummaryTable entries={entries} />
								<VendorLedgerPaymentsList entries={entries} />
								<Card className="bg-white dark:bg-gray-800">
									<CardBody>
										<VendorLedgerPipeline entries={entries} />
									</CardBody>
								</Card>
							</>
						) : (
							<NotFound title="No ledger entries found for this vendor." />
						)}
					</DataWrapper>
				</>
			)}
			</>
			)}
		</>
	);
};

export default VendorLedger;
